import { useState } from 'react'
import { CONTENT_PAGE_HEADERS } from "~/lib/cache.server"
import type { MetaFunction, ActionFunctionArgs } from 'react-router'
import { useLoaderData, Link, Form, useActionData, useNavigation } from 'react-router'
import { db } from '@afficheservices/db'
import { questions, serviceCategories } from '@afficheservices/db/schema'
import { desc, eq, isNull } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { MessageSquare, Clock, Eye, Plus } from 'lucide-react'
import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'

export function headers() { return CONTENT_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Questions aux artisans — Posez votre question | AfficheServices' },
  { name: 'description', content: 'Posez vos questions travaux et recevez des réponses d\'artisans vérifiés. Plomberie, électricité, rénovation — conseils gratuits.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/questions' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Questions+artisans&subtitle=105+questions' },
]

export async function loader() {
  const allQuestions = await db
    .select({
      id: questions.id,
      title: questions.title,
      slug: questions.slug,
      body: questions.body,
      guestName: questions.guestName,
      categoryName: serviceCategories.nameFr,
      answerCount: questions.answerCount,
      viewCount: questions.viewCount,
      createdAt: questions.createdAt,
    })
    .from(questions)
    .leftJoin(serviceCategories, eq(serviceCategories.id, questions.categoryId))
    .where(eq(questions.status, 'published'))
    .orderBy(desc(questions.createdAt))
    .limit(30)

  const categories = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr })
    .from(serviceCategories)
    .where(isNull(serviceCategories.parentId))
    .orderBy(serviceCategories.displayOrder)

  return { questions: allQuestions, categories }
}

function slugify(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 100)
}

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'form')) return rateLimitResponse()
  const formData = await request.formData()
  const title = (formData.get('title') as string)?.replace(/<[^>]*>/g, '').replace(/javascript:/gi, '').trim()
  const body = (formData.get('body') as string)?.replace(/<[^>]*>/g, '').replace(/javascript:/gi, '').trim()
  const name = (formData.get('name') as string)?.trim()
  const categoryId = formData.get('categoryId') as string

  if (!title || title.length < 10) return { error: 'Le titre doit contenir au moins 10 caractères' }
  if (!body || body.length < 20) return { error: 'La question doit contenir au moins 20 caractères' }
  if (!name) return { error: 'Votre nom est requis' }

  const slug = slugify(title) + '-' + Date.now().toString(36)

  await db.insert(questions).values({
    title,
    body,
    guestName: name,
    slug,
    categoryId: categoryId || null,
    status: 'published',
  })

  return { success: true }
}

export default function QuestionsIndex() {
  const { questions: questionList, categories } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const [showForm, setShowForm] = useState(false)

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Questions aux artisans</h1>
            <p className="mt-2 text-sm text-slate-500">Posez vos questions travaux et recevez des réponses d'experts</p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover btn-press"
          >
            <Plus className="h-4 w-4" /> Poser une question
          </button>
        </div>

        {/* Ask question form */}
        {showForm && (
          <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-sm">
            {actionData?.success ? (
              <div className="text-center py-4">
                <MessageSquare className="mx-auto h-10 w-10 text-emerald-500" />
                <p className="mt-2 text-sm font-semibold text-slate-900">Question publiée !</p>
                <p className="mt-1 text-xs text-slate-500">Les artisans vérifiés vous répondront bientôt.</p>
              </div>
            ) : (
              <Form method="post" className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Votre question *</label>
                  <input name="title" required minLength={10} placeholder="Ex: Comment choisir un bon plombier à Paris ?"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Détails *</label>
                  <textarea name="body" required minLength={20} rows={3} placeholder="Décrivez votre situation en détail..."
                    className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Votre nom *</label>
                    <input name="name" required placeholder="Prénom"
                      className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Catégorie</label>
                    <select name="categoryId"
                      className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-primary focus:outline-none">
                      <option value="">Toutes</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                {actionData?.error && <p className="text-sm text-red-500">{actionData.error}</p>}
                <button type="submit" disabled={navigation.state === 'submitting'}
                  className="rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50 btn-press">
                  {navigation.state === 'submitting' ? 'Publication...' : 'Publier ma question'}
                </button>
              </Form>
            )}
          </div>
        )}

        {/* Questions list */}
        <div className="mt-8 space-y-3">
          {questionList.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-border bg-white p-12 text-center">
              <MessageSquare className="mx-auto h-12 w-12 text-slate-300" />
              <h2 className="mt-4 text-lg font-semibold text-slate-900">Aucune question pour le moment</h2>
              <p className="mt-2 text-sm text-slate-500">Soyez le premier à poser une question !</p>
            </div>
          ) : questionList.map((q) => (
            <Link
              key={q.id}
              to={`/questions/${q.slug}`}
              className="card-hover flex items-start gap-4 rounded-xl border border-border bg-white p-5 shadow-xs"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary">
                {q.answerCount}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-slate-900 group-hover:text-primary">{q.title}</h3>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{q.body}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                  {q.categoryName && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">{q.categoryName}</span>}
                  <span>{q.guestName ?? 'Anonyme'}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(q.createdAt).toLocaleDateString('fr-FR')}</span>
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {q.viewCount} vues</span>
                  <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {q.answerCount} réponse{q.answerCount > 1 ? 's' : ''}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  )
}
